// Section « Adresse d'alerte » de l'écran « Mes clés » (décision O107, chemin B). Une clé de sécurité n'est enregistrée que si
// Ekera sait où alerter la personne. Deux chemins : l'adresse du fournisseur d'identité (revendication `email` vérifiée, d'un
// domaine du client) ou une adresse déclarée par un administrateur puis confirmée par la personne avec un code reçu par
// courriel à cette adresse.
// Le code n'est JAMAIS affiché ni gardé par l'application : il n'existe que dans le courriel ; la personne le saisit, il part
// dans le corps d'une requête sans cache, et le champ est vidé dès la réponse. Rien n'est écrit dans localStorage,
// sessionStorage ni le cache du navigateur. Aucune suppression : une adresse se désactive ou s'annule (l'historique reste).
import { useQuery } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import type { EnvoiCode, VueAdresse, VueAdresseAdmin } from '../api/types';
import { Chargement, Erreur, Etiquette } from '../composants/communs';
import { useEkera } from '../contexte';
import {
  adresseBienFormee,
  annulable,
  attendUnCode,
  codeAdresseValide,
  desactivable,
  ETATS_ADRESSE,
  etatAdresseAffiche,
  normaliserCodeAdresse,
  ordonner,
  resumeAdresse,
  tonAdresse,
} from '../domaine/adresses';
import { dateFr } from '../domaine/format';
import { FormulaireMotif, MessageErreur, useAction, useRelireCles } from './ClesCommuns';

/** « Code envoyé… » : sans jamais le code. */
function texteEnvoi(adresse: string, e: EnvoiCode): string {
  return `Un code a été envoyé à ${adresse} (valable jusqu’au ${dateFr(e.code_expire_le)}, trois essais). Il n’est pas affiché ici : lisez-le dans le courriel. Il reste ${e.envois_restants} envoi${e.envois_restants > 1 ? 's' : ''} possible${e.envois_restants > 1 ? 's' : ''}.`;
}

// ---------------------------------------------------------------- la personne

export function AdresseAlerte() {
  const { moi } = useEkera();
  const etat = moi.webauthn?.adresse_alerte;
  const resume = resumeAdresse(etat, dateFr);
  if (!etat || !resume) return null;
  const vues = ordonner(etat.adresses);
  return (
    <section className="panneau pile" aria-labelledby="t-adresse-alerte" data-testid="adresse-alerte">
      <h2 id="t-adresse-alerte" tabIndex={-1}>
        Adresse d’alerte
      </h2>
      <p className={`encadre ${resume.ton}`} role="status" data-testid="resume-adresse">
        {resume.texte}
      </p>
      {resume.demanderUnAdministrateur && (
        <div className="encadre info pile" style={{ gap: 6 }} data-testid="identifiant-pour-administrateur">
          <p>
            Donnez à l’administrateur de quoi vous désigner, <b>sans ambiguïté</b> (le même identifiant chez un autre fournisseur est une autre personne) :
          </p>
          <ul className="liste-nue">
            <li>
              identifiant (« sub ») : <span className="num">{moi.sujet}</span>
            </li>
            {moi.emetteur && (
              <li>
                émetteur : <span className="num">{moi.emetteur}</span>
              </li>
            )}
          </ul>
          <p className="note">
            L’administrateur déclare votre adresse professionnelle ; Ekera vous envoie un code à cette adresse. Tant que vous ne l’avez pas confirmée, elle ne
            reçoit que ce code, aucune alerte de sécurité.
          </p>
        </div>
      )}
      {vues.length > 0 && (
        <ul className="liste-nue pile" style={{ gap: 10 }} data-testid="mes-adresses">
          {vues.map((v) => (
            <LigneAdresse key={v.adresse_id} vue={v} />
          ))}
        </ul>
      )}
    </section>
  );
}

function LigneAdresse({ vue }: { vue: VueAdresse }) {
  const { client } = useEkera();
  const relire = useRelireCles();
  const { occupe, erreur, executer } = useAction();
  const [code, setCode] = useState('');
  const [annulation, setAnnulation] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const idCode = useId();
  const attend = attendUnCode(vue.etat);

  async function confirmer(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    const saisi = code;
    // le code ne reste dans aucun état de l'écran une fois parti, quelle que soit la réponse
    setCode('');
    const r = await executer('confirmation', () => client.confirmerAdresse(vue.adresse_id, saisi));
    if (r) {
      setMessage(
        r.etat === 'en_carence'
          ? `Adresse confirmée, en délai de sécurité${r.active_le ? ` jusqu’au ${dateFr(r.active_le)}` : ''} : d’ici là, votre ancienne adresse reste destinataire de toutes vos alertes ; la nouvelle ne reçoit que les alertes qui concernent les adresses. Si vous n’êtes pas à l’origine de ce changement, annulez-le ci-dessous.`
          : `Adresse confirmée : vos alertes de sécurité partent désormais vers ${r.adresse}.`,
      );
      await relire();
    }
  }

  async function renvoyer() {
    setMessage(null);
    const r = await executer('code', () => client.codeAdresse(vue.adresse_id));
    if (r) {
      setMessage(texteEnvoi(vue.adresse, r));
      await relire();
    }
  }

  async function annuler(motif: string) {
    setMessage(null);
    const r = await executer('annulation', () => client.annulerMonAdresse(vue.adresse_id, motif || undefined));
    if (r) {
      setAnnulation(false);
      setMessage(`L’adresse ${vue.adresse} est annulée. Elle reste dans l’historique et ne reçoit rien.`);
      await relire();
    }
  }

  return (
    <li className="encadre info pile" style={{ gap: 8 }} data-testid={`adresse-${vue.adresse_id}`}>
      <p>
        <b>{vue.adresse}</b> · <Etiquette ton={tonAdresse(vue.etat)}>{etatAdresseAffiche(vue.etat)}</Etiquette> · déclarée le {dateFr(vue.declaree_le)}
        {vue.remplace_une_adresse ? ' · remplacera votre adresse actuelle' : ''}
      </p>
      {attend && (
        <form className="pile" style={{ gap: 8 }} onSubmit={(e) => void confirmer(e)}>
          <p className="note">
            Un administrateur a déclaré cette adresse pour vous{vue.expire_le ? ` (déclaration valable jusqu’au ${dateFr(vue.expire_le)})` : ''}. Ekera
            lui a envoyé un code par courriel : <b>lisez-le dans cette boîte, il n’est jamais affiché ici</b>. Il vaut 15 minutes et 3 essais.
            {vue.code_expire_le ? ` Dernier code valable jusqu’au ${dateFr(vue.code_expire_le)}.` : ' Aucun code valable : demandez-en un.'}
          </p>
          <div className="champ" style={{ maxWidth: 320 }}>
            <label htmlFor={idCode}>
              Code reçu à {vue.adresse} <span className="aide">(« XXXX-XXXX »)</span>
            </label>
            <input
              id={idCode}
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={code}
              maxLength={9}
              onChange={(e) => setCode(normaliserCodeAdresse(e.target.value))}
            />
          </div>
          <div className="ligne">
            <button type="submit" className="btn principal" disabled={!!occupe || !codeAdresseValide(code)}>
              {occupe === 'confirmation' ? 'Vérification…' : 'Confirmer cette adresse'}
            </button>
            <button type="button" className="btn petit" disabled={!!occupe} onClick={() => void renvoyer()}>
              {occupe === 'code' ? 'Envoi…' : 'Renvoyer le code'}
            </button>
          </div>
        </form>
      )}
      {vue.etat === 'en_carence' && (
        <p className="note" data-testid="adresse-carence">
          Confirmée, mais en délai de sécurité{vue.active_le ? ` jusqu’au ${dateFr(vue.active_le)}` : ''}. D’ici là, <b>votre ancienne adresse reste
          destinataire de toutes vos alertes</b> ; celle-ci ne reçoit que les alertes d’adresse. Si ce changement n’est pas de vous, annulez-le.
        </p>
      )}
      {vue.etat === 'invalide' && (
        <p className="note">Les données de cette adresse ont été modifiées en dehors de l’application : elle ne sert plus. Demandez à un administrateur d’en déclarer une nouvelle.</p>
      )}
      {vue.etat === 'expiree' && <p className="note">La déclaration n’a pas été confirmée en 72 heures : un administrateur doit la déclarer de nouveau.</p>}
      {annulable(vue.etat) && (
        <div className="ligne">
          <button type="button" className="btn petit" disabled={!!occupe} onClick={() => setAnnulation(true)}>
            {attend ? 'Annuler cette déclaration…' : 'Annuler ce changement d’adresse…'}
          </button>
        </div>
      )}
      {annulation && (
        <FormulaireMotif
          facultatif
          libelle="Motif de l’annulation"
          bouton="Confirmer l’annulation"
          occupe={occupe === 'annulation'}
          surConfirmer={(m) => void annuler(m)}
          surAnnuler={() => setAnnulation(false)}
          aide="L’annulation est immédiate et sans geste ; l’adresse reste dans l’historique."
        />
      )}
      <MessageErreur erreur={erreur} titre="L’opération n’a pas abouti." />
      {message && (
        <p className="encadre ok" role="status" data-testid="message-adresse">
          {message}
        </p>
      )}
    </li>
  );
}

// ---------------------------------------------------------------- l'administration

export function AdressesDuClient() {
  const { client } = useEkera();
  const [etat, setEtat] = useState('');
  const adresses = useQuery({ queryKey: ['adresses-client', etat], queryFn: () => client.adressesDuClient(etat || undefined) });
  const idEtat = useId();
  return (
    <section className="panneau pile" aria-labelledby="t-adresses-client" data-testid="adresses-client">
      <h2 id="t-adresses-client">Adresses d’alerte des personnes (administration)</h2>
      <p className="note">
        Si le fournisseur d’identité de l’entreprise n’atteste pas l’adresse de courriel d’une personne, vous la <b>déclarez</b> ici ; la personne la
        confirme avec un code envoyé à cette adresse. Vous ne voyez jamais ce code. Une adresse non confirmée ne reçoit aucune alerte de sécurité.
      </p>
      <DeclarationAdresse />
      <div className="champ" style={{ maxWidth: 280 }}>
        <label htmlFor={idEtat}>Adresses à afficher</label>
        <select id={idEtat} value={etat} onChange={(e) => setEtat(e.target.value)}>
          <option value="">Toutes</option>
          {ETATS_ADRESSE.map((e) => (
            <option key={e} value={e}>
              {etatAdresseAffiche(e)}
            </option>
          ))}
        </select>
      </div>
      {adresses.isPending && <Chargement quoi="des adresses d’alerte" />}
      {adresses.error && <Erreur erreur={adresses.error} titre="Impossible de lire les adresses d’alerte." />}
      {adresses.data && adresses.data.length === 0 && <p className="note">Aucune adresse dans cet état.</p>}
      {adresses.data && adresses.data.length > 0 && (
        <ul className="liste-nue pile" style={{ gap: 12 }} data-testid="liste-adresses-client">
          {adresses.data.map((a) => (
            <LigneAdresseAdmin key={a.adresse_id} adresse={a} />
          ))}
        </ul>
      )}
    </section>
  );
}

function DeclarationAdresse() {
  const { client, moi } = useEkera();
  const relire = useRelireCles();
  const { occupe, erreur, executer } = useAction();
  const [sujet, setSujet] = useState('');
  const [adresse, setAdresse] = useState('');
  const [verif, setVerif] = useState(false);
  const [relu, setRelu] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const idSujet = useId();
  const idAdresse = useId();
  const idRelu = useId();
  const pret = sujet.trim().length > 0 && sujet.trim().length <= 255 && adresseBienFormee(adresse);

  async function declarer() {
    setMessage(null);
    const r = await executer('declaration', () => client.declarerAdresse(sujet.trim(), adresse.trim()));
    if (r) {
      setVerif(false);
      setRelu(false);
      setSujet('');
      setAdresse('');
      setMessage(
        `Adresse ${r.adresse} déclarée pour ${r.sujet}. Un code lui a été envoyé (valable 15 minutes, trois essais ; il reste ${r.envois_restants} envoi${r.envois_restants > 1 ? 's' : ''} possible${r.envois_restants > 1 ? 's' : ''}). Vous ne le voyez pas : la personne le lit dans sa boîte et le saisit dans son écran « Mes clés » ; la déclaration expire au bout de 72 heures.`,
      );
      await relire();
    }
  }

  return (
    <form
      className="pile"
      style={{ gap: 10 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (pret) setVerif(true);
      }}
      data-testid="declaration-adresse"
    >
      <h3>Déclarer l’adresse d’une personne</h3>
      <div className="champ" style={{ maxWidth: 460 }}>
        <label htmlFor={idSujet}>
          Identifiant de la personne (« sub ») <span className="aide">(celui que la personne lit dans son écran « Mes clés » ; pas le vôtre)</span>
        </label>
        <input id={idSujet} type="text" value={sujet} maxLength={255} autoComplete="off" spellCheck={false} onChange={(e) => { setSujet(e.target.value); setVerif(false); setRelu(false); }} />
      </div>
      <p className="note">
        Émetteur : <span className="num">{moi.emetteur ?? 'celui de votre propre connexion'}</span> (la personne est désignée chez le même fournisseur d’identité
        que vous ; un même identifiant chez un autre émetteur est une autre personne).
      </p>
      <div className="champ" style={{ maxWidth: 460 }}>
        <label htmlFor={idAdresse}>
          Adresse de courriel <span className="aide">(d’un domaine déclaré pour l’entreprise ; elle recevra seulement un code tant qu’elle n’est pas confirmée)</span>
        </label>
        <input id={idAdresse} type="text" inputMode="email" value={adresse} maxLength={254} autoComplete="off" spellCheck={false} onChange={(e) => { setAdresse(e.target.value); setVerif(false); setRelu(false); }} />
      </div>
      {!verif && (
        <div className="ligne">
          <button type="submit" className="btn principal" disabled={!pret || !!occupe}>
            Vérifier avant d’envoyer
          </button>
        </div>
      )}
      {verif && (
        <div className="encadre attention pile" style={{ gap: 8 }} role="group" aria-label="Confirmation de la déclaration" data-testid="confirmer-declaration">
          <p data-testid="recapitulatif-declaration">
            Vous allez envoyer un code à <b>{adresse.trim()}</b> pour l’identité{' '}
            <b className="num">
              {moi.emetteur ?? 'émetteur de votre connexion'} / {sujet.trim()}
            </b>
            . <b>Aucune alerte de sécurité n’y partira</b> avant que la personne ait confirmé l’adresse.
          </p>
          <p className="note">
            Un identifiant (« sub ») mal recopié désigne une autre personne, ou personne : le code partirait vers une boîte qui ne pourrait pas le
            saisir. Comparez-le, caractère par caractère, avec celui que la personne lit dans son écran « Mes clés ».
          </p>
          <label className="ligne" htmlFor={idRelu} style={{ alignItems: 'flex-start' }}>
            <input id={idRelu} type="checkbox" checked={relu} onChange={(e) => setRelu(e.target.checked)} />
            <span>J’ai relu l’identifiant et l’émetteur : ils sont ceux de la personne.</span>
          </label>
          <div className="ligne">
            <button type="button" className="btn principal" disabled={!!occupe || !relu} onClick={() => void declarer()}>
              {occupe === 'declaration' ? 'Envoi…' : 'Confirmer la déclaration et envoyer le code'}
            </button>
            <button type="button" className="btn petit" disabled={!!occupe} onClick={() => { setVerif(false); setRelu(false); }}>
              Modifier
            </button>
          </div>
        </div>
      )}
      <MessageErreur erreur={erreur} titre="L’adresse n’a pas été déclarée." />
      {message && (
        <p className="encadre ok" role="status" data-testid="message-declaration">
          {message}
        </p>
      )}
    </form>
  );
}

function LigneAdresseAdmin({ adresse }: { adresse: VueAdresseAdmin }) {
  const { client } = useEkera();
  const relire = useRelireCles();
  const { occupe, erreur, executer } = useAction();
  const [motif, setMotif] = useState<'annulation' | 'desactivation' | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function renvoyer() {
    setMessage(null);
    const r = await executer('code', () => client.codeAdresseAdmin(adresse.adresse_id));
    if (r) {
      setMessage(texteEnvoi(adresse.adresse, r));
      await relire();
    }
  }

  async function avecMotif(m: string) {
    const nature = motif;
    setMessage(null);
    const r = await executer(nature ?? 'motif', () => (nature === 'annulation' ? client.annulerAdresse(adresse.adresse_id, m) : client.desactiverAdresse(adresse.adresse_id, m)));
    if (r) {
      setMotif(null);
      setMessage(nature === 'annulation' ? 'Déclaration annulée : elle reste dans l’historique.' : 'Adresse désactivée : elle ne reçoit plus rien et reste dans l’historique.');
      await relire();
    }
  }

  return (
    <li className="encadre info pile" style={{ gap: 8 }} data-testid={`adresse-client-${adresse.adresse_id}`}>
      <p>
        <b>{adresse.adresse}</b> · <Etiquette ton={tonAdresse(adresse.etat)}>{etatAdresseAffiche(adresse.etat)}</Etiquette>
      </p>
      <p className="note">
        Déclarée le {dateFr(adresse.declaree_le)} par {adresse.declaree_par}
        {adresse.etat === 'declaree' && adresse.expire_le ? ` · à confirmer avant le ${dateFr(adresse.expire_le)}` : ''}
        {adresse.etat === 'en_carence' && adresse.active_le ? ` · utilisée à partir du ${dateFr(adresse.active_le)} (l’ancienne reste destinataire d’ici là)` : ''}
        {adresse.remplace_une_adresse ? ' · remplace une adresse confirmée' : ''}
      </p>
      <details className="note">
        <summary>Détails techniques</summary>
        Identifiant de la personne : <span className="num">{adresse.sujet}</span>
      </details>
      {adresse.etat === 'invalide' && (
        <p className="encadre erreur">Les données de cette adresse ont été modifiées en dehors de l’application (scellé rompu) : elle est inutilisable. Vérifiez l’accès à la base avec votre RSSI.</p>
      )}
      <div className="ligne">
        {attendUnCode(adresse.etat) && (
          <button type="button" className="btn petit" disabled={!!occupe} onClick={() => void renvoyer()}>
            {occupe === 'code' ? 'Envoi…' : 'Renvoyer le code'}
          </button>
        )}
        {annulable(adresse.etat) && (
          <button type="button" className="btn petit" disabled={!!occupe} onClick={() => setMotif('annulation')}>
            Annuler la déclaration…
          </button>
        )}
        {desactivable(adresse.etat) && (
          <button type="button" className="btn petit" disabled={!!occupe} onClick={() => setMotif('desactivation')}>
            Désactiver…
          </button>
        )}
      </div>
      {motif && (
        <FormulaireMotif
          libelle={motif === 'annulation' ? 'Motif de l’annulation' : 'Motif de la désactivation'}
          bouton={motif === 'annulation' ? 'Confirmer l’annulation' : 'Confirmer la désactivation'}
          occupe={occupe === motif}
          surConfirmer={(m) => void avecMotif(m)}
          surAnnuler={() => setMotif(null)}
          aide={
            motif === 'desactivation'
              ? 'L’adresse ne recevra plus d’alerte. Refusé si elle protège seule une clé de sécurité de la personne : déclarez d’abord la nouvelle adresse.'
              : 'L’annulation est immédiate et sans geste ; l’adresse reste dans l’historique.'
          }
        />
      )}
      <MessageErreur erreur={erreur} titre="L’opération n’a pas abouti." />
      {message && (
        <p className="encadre ok" role="status">
          {message}
        </p>
      )}
    </li>
  );
}
